<template>
  <div class="product-page" :style="themeVars">
    <section class="product-hero">
      <div class="product-hero__bg"></div>
      <div class="product-hero__grid-lines"></div>
      <div class="product-hero__overlay"></div>
      <div class="container product-hero__wrap">
        <div class="product-hero__main">
          <div class="product-hero__content">
            <div class="product-hero__badge">{{ pageMeta.badge }}</div>
            <h1 class="product-hero__title">{{ pageMeta.title }}</h1>
            <p class="product-hero__desc">{{ pageMeta.description }}</p>
            <div class="product-hero__actions">
              <router-link :to="`${contactPath}#contact`" class="btn btn-primary btn-hero">预约演示 →</router-link>
              <a href="#features" class="btn btn-outline btn-hero">了解产品功能</a>
            </div>
          </div>
          <HeroVisual class="product-hero__visual" />
        </div>
        <div class="product-hero__stats">
          <div class="product-hero__stats-grid">
            <div
              v-for="(item, index) in pageMeta.stats"
              :key="item.label"
              class="product-hero__stat"
            >
              <div class="product-hero__stat-value">{{ item.value }}</div>
              <div class="product-hero__stat-label">{{ item.label }}</div>
              <span v-if="index < pageMeta.stats.length - 1" class="product-hero__stat-divider" aria-hidden="true"></span>
            </div>
          </div>
        </div>
      </div>
    </section>

    <section id="features" class="section product-features">
      <div class="container">
        <div class="section-header">
          <h2 class="section-title">{{ sections.features.title }}</h2>
          <p class="section-desc">{{ sections.features.desc }}</p>
        </div>
        <div class="product-features__grid">
          <div v-for="item in features" :key="item.title" class="product-features__card">
            <div class="product-features__icon">{{ item.icon }}</div>
            <h3>{{ item.title }}</h3>
            <p>{{ item.desc }}</p>
          </div>
        </div>
      </div>
    </section>

    <section class="section product-arch">
      <div class="container">
        <div class="section-header">
          <h2 class="section-title">方案架构</h2>
          <p class="section-desc">{{ architecture.desc }}</p>
        </div>
        <PartyArchDiagram
          v-if="architecture.variant === 'party'"
          :architecture="architecture"
        />
        <InspectionArchDiagram
          v-else-if="architecture.variant === 'inspection'"
          :architecture="architecture"
        />
        <VillageArchDiagram
          v-else-if="architecture.variant === 'village'"
          :architecture="architecture"
        />
        <div v-else-if="architecture.variant === 'gov-bar'" class="gov-arch">
          <h3 class="gov-arch__title">{{ architecture.title }}</h3>
          <div class="gov-arch__body">
            <div
              v-for="layer in architecture.layers"
              :key="layer.name"
              class="gov-arch__row"
            >
              <div class="gov-arch__label">{{ layer.name }}</div>
              <div class="gov-arch__panel">
                <div v-if="layer.groups" class="gov-arch__platform">
                  <div
                    v-for="(group, groupIndex) in layer.groups"
                    :key="groupIndex"
                    class="gov-arch__platform-group"
                  >
                    <div v-if="group.name" class="gov-arch__platform-title">{{ group.name }}</div>
                    <div
                      :class="[
                        'gov-arch__cells',
                        group.name ? 'gov-arch__cells--stack' : 'gov-arch__cells--grid'
                      ]"
                    >
                      <span
                        v-for="item in group.items"
                        :key="item"
                        class="gov-arch__cell"
                      >{{ item }}</span>
                    </div>
                  </div>
                </div>
                <div v-else-if="layer.header" class="gov-arch__stack">
                  <div class="gov-arch__bar">{{ layer.header }}</div>
                  <div class="gov-arch__cells gov-arch__cells--row">
                    <span
                      v-for="item in layer.items"
                      :key="item"
                      class="gov-arch__cell"
                    >{{ item }}</span>
                  </div>
                </div>
                <div v-else-if="layer.footer" class="gov-arch__stack">
                  <div class="gov-arch__cells gov-arch__cells--row">
                    <span
                      v-for="item in layer.items"
                      :key="item"
                      class="gov-arch__cell"
                    >{{ item }}</span>
                  </div>
                  <div class="gov-arch__bar">{{ layer.footer }}</div>
                </div>
                <div v-else class="gov-arch__cells gov-arch__cells--row">
                  <span
                    v-for="item in layer.items"
                    :key="item"
                    class="gov-arch__cell"
                  >{{ item }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div v-else class="product-arch__diagram">
          <div
            v-for="(layer, index) in architecture.layers"
            :key="layer.name"
            :class="['product-arch__layer', `product-arch__layer--${index}`]"
          >
            <span v-if="layer.header" class="product-arch__layer-header">{{ layer.header }}</span>
            <span class="product-arch__layer-name">{{ layer.name }}</span>
            <span v-if="layer.subtitle" class="product-arch__layer-subtitle">{{ layer.subtitle }}</span>
            <div v-if="layer.groups" class="product-arch__layer-groups">
              <div
                v-for="(group, groupIndex) in layer.groups"
                :key="groupIndex"
                class="product-arch__group"
              >
                <span v-if="group.name" class="product-arch__group-name">{{ group.name }}</span>
                <div class="product-arch__layer-items">
                  <span v-for="item in group.items" :key="item">{{ item }}</span>
                </div>
              </div>
            </div>
            <div v-else class="product-arch__layer-items">
              <span v-for="item in layer.items" :key="item">{{ item }}</span>
            </div>
          </div>
        </div>
        <p class="product-arch__footer">{{ architecture.footer }}</p>
      </div>
    </section>

    <section class="section product-why">
      <div class="container">
        <div class="section-header">
          <h2 class="section-title">{{ sections.whyUs.title }}</h2>
          <p class="section-desc">{{ sections.whyUs.desc }}</p>
        </div>
        <div class="product-why__grid">
          <div v-for="item in whyUs" :key="item.title" class="product-why__card">
            <h3>{{ item.title }}</h3>
            <p>{{ item.desc }}</p>
          </div>
        </div>
      </div>
    </section>

    <section class="section product-scenario">
      <div class="container">
        <span class="section-label">{{ scenarios.label }}</span>
        <div class="section-header">
          <h2 class="section-title">{{ scenarios.title }}</h2>
          <p class="section-desc">{{ scenarios.desc }}</p>
        </div>
        <div class="product-scenario__grid">
          <div v-for="item in scenarios.items" :key="item.title" class="product-scenario__card">
            <h3>{{ item.title }}</h3>
            <p>{{ item.desc }}</p>
          </div>
        </div>
        <div class="product-scenario__highlights">
          <div v-for="item in scenarios.highlights" :key="item.title" class="product-scenario__highlight">
            <h4>{{ item.title }}</h4>
            <p>{{ item.desc }}</p>
          </div>
        </div>
      </div>
    </section>

    <section class="section product-related">
      <div class="container">
        <div class="section-header">
          <h2 class="section-title">{{ sections.related.title }}</h2>
          <p class="section-desc">{{ sections.related.desc }}</p>
        </div>
        <div class="product-related__grid">
          <component
            :is="item.route ? 'router-link' : 'a'"
            v-for="item in relatedProducts"
            :key="item.title"
            :to="item.route || undefined"
            :href="item.route ? undefined : `${contactPath}#contact`"
            class="product-related__card"
          >
            <div class="product-related__icon" :style="{ background: item.color + '18', color: item.color }">
              {{ item.icon }}
            </div>
            <h3>{{ item.title }}</h3>
            <p>{{ item.desc }}</p>
            <span class="product-related__link" :style="{ color: item.color }">了解详情 →</span>
          </component>
        </div>
      </div>
    </section>

    <ContactForm :default-product="defaultProduct" />
  </div>
</template>

<script>
import ContactForm from './ContactForm.vue'
import HeroVisual from './HeroVisual.vue'
import VillageArchDiagram from './VillageArchDiagram.vue'
import InspectionArchDiagram from './InspectionArchDiagram.vue'
import PartyArchDiagram from './PartyArchDiagram.vue'

export default {
  name: 'ProductPage',
  components: { ContactForm, HeroVisual, VillageArchDiagram, InspectionArchDiagram, PartyArchDiagram },
  props: {
    theme: {
      type: Object,
      required: true
    },
    pageMeta: {
      type: Object,
      required: true
    },
    sections: {
      type: Object,
      required: true
    },
    features: {
      type: Array,
      required: true
    },
    architecture: {
      type: Object,
      required: true
    },
    whyUs: {
      type: Array,
      required: true
    },
    scenarios: {
      type: Object,
      required: true
    },
    relatedProducts: {
      type: Array,
      required: true
    },
    contactPath: {
      type: String,
      required: true
    },
    defaultProduct: {
      type: String,
      required: true
    }
  },
  computed: {
    themeVars() {
      return {
        '--theme-primary': this.theme.primary,
        '--theme-primary-light': this.theme.primaryLight,
        '--theme-shadow': this.theme.shadow,
        '--theme-gradient': this.theme.gradient,
        '--theme-overlay': this.theme.overlay
      }
    }
  }
}
</script>

<style scoped>
.product-hero {
  position: relative;
  padding: 88px 0 56px;
  overflow: hidden;
}

.product-hero__bg {
  position: absolute;
  inset: 0;
  background: var(--theme-gradient);
}

.product-hero__grid-lines {
  position: absolute;
  inset: 0;
  background-image:
    linear-gradient(rgba(255, 255, 255, 0.03) 1px, transparent 1px),
    linear-gradient(90deg, rgba(255, 255, 255, 0.03) 1px, transparent 1px);
  background-size: 60px 60px;
  mask-image: radial-gradient(ellipse 80% 70% at 50% 50%, black 20%, transparent 80%);
}

.product-hero__overlay {
  position: absolute;
  inset: 0;
  background: var(--theme-overlay);
}

.product-hero__wrap {
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  gap: 36px;
}

.product-hero__main {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 40px;
  align-items: center;
  padding-top: 16px;
}

.product-hero__content {
  max-width: 560px;
  padding: 0 0 0 8px;
}

.product-hero__visual {
  justify-self: center;
}

.product-hero__badge {
  display: inline-block;
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 24px;
  padding: 8px 18px;
  font-size: 13px;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.95);
  margin-bottom: 22px;
  backdrop-filter: blur(12px);
}

.product-hero__title {
  font-size: 42px;
  font-weight: 800;
  color: #fff;
  line-height: 1.2;
  margin-bottom: 18px;
  letter-spacing: -0.5px;
}

.product-hero__desc {
  font-size: 16px;
  color: rgba(255, 255, 255, 0.88);
  line-height: 1.8;
  margin-bottom: 32px;
}

.product-hero__actions {
  display: flex;
  gap: 16px;
  flex-wrap: wrap;
}

.product-hero__stats {
  padding: 0 8px;
}

.product-hero__stats-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  background: rgba(0, 0, 0, 0.18);
  backdrop-filter: blur(16px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 14px;
  overflow: hidden;
}

.product-hero__stat {
  position: relative;
  padding: 22px 20px;
  text-align: center;
}

.product-hero__stat-divider {
  position: absolute;
  right: 0;
  top: 50%;
  transform: translateY(-50%);
  width: 1px;
  height: 36px;
  background: rgba(255, 255, 255, 0.12);
}

.product-hero__stat-value {
  font-size: 28px;
  font-weight: 800;
  color: #fff;
  margin-bottom: 4px;
}

.product-hero__stat-label {
  font-size: 13px;
  color: rgba(255, 255, 255, 0.7);
}

.product-features {
  background: #fff;
}

.product-features__grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 24px;
}

.product-features__card {
  background: #f7f9fc;
  border: 1px solid #eef1f6;
  border-radius: 12px;
  padding: 28px;
  transition: box-shadow 0.3s, transform 0.3s;
}

.product-features__card:hover {
  box-shadow: 0 8px 32px var(--theme-shadow);
  transform: translateY(-2px);
}

.product-features__icon {
  width: 48px;
  height: 48px;
  background: #fff;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
  margin-bottom: 16px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
}

.product-features__card h3 {
  font-size: 17px;
  font-weight: 700;
  color: #1a1a2e;
  margin-bottom: 10px;
}

.product-features__card p {
  font-size: 14px;
  color: #666;
  line-height: 1.7;
}

.product-arch {
  background: #fff;
}

/* 综合治理方案架构图 */
.gov-arch {
  max-width: 1200px;
  margin: 0 auto;
  border: 1px solid #f0d0d0;
  border-radius: 4px;
  overflow: hidden;
  background: #fff;
}

.gov-arch__title {
  margin: 0;
  padding: 18px 24px;
  font-size: 22px;
  font-weight: 700;
  color: #c62828;
  text-align: center;
  border-bottom: 1px solid #f0d0d0;
  background: #fff;
}

.gov-arch__body {
  display: flex;
  flex-direction: column;
}

.gov-arch__row {
  display: flex;
  border-bottom: 1px solid #f0d0d0;
}

.gov-arch__row:last-child {
  border-bottom: none;
}

.gov-arch__label {
  flex: 0 0 52px;
  display: flex;
  align-items: center;
  justify-content: center;
  writing-mode: vertical-rl;
  text-orientation: mixed;
  background: #d32f2f;
  color: #fff;
  font-size: 15px;
  font-weight: 700;
  letter-spacing: 4px;
  padding: 16px 0;
}

.gov-arch__panel {
  flex: 1;
  padding: 14px 16px;
  background: #fff5f5;
  min-width: 0;
}

.gov-arch__cells {
  display: flex;
  gap: 10px;
}

.gov-arch__cells--row {
  flex-wrap: wrap;
}

.gov-arch__cells--row .gov-arch__cell {
  flex: 1 1 0;
  min-width: 120px;
}

.gov-arch__cells--grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 10px;
}

.gov-arch__cells--stack {
  flex-direction: column;
}

.gov-arch__cell {
  background: #fff;
  border: 1px solid #ef9a9a;
  color: #c62828;
  font-size: 13px;
  font-weight: 600;
  text-align: center;
  padding: 10px 8px;
  line-height: 1.4;
  border-radius: 2px;
}

.gov-arch__stack {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.gov-arch__bar {
  background: #fff;
  border: 1px solid #ef9a9a;
  color: #c62828;
  font-size: 14px;
  font-weight: 700;
  text-align: center;
  padding: 10px 16px;
  border-radius: 2px;
}

.gov-arch__platform {
  display: grid;
  grid-template-columns: 2.2fr 1fr 1fr;
  gap: 0;
  align-items: stretch;
}

.gov-arch__platform-group {
  border: 1px dashed #5c9fd4;
  border-radius: 4px;
  padding: 12px;
  background: rgba(255, 255, 255, 0.65);
  min-height: 100%;
}

.gov-arch__platform-group:not(:last-child) {
  position: relative;
  margin-right: 28px;
}

.gov-arch__platform-group:not(:last-child)::after {
  content: '→';
  position: absolute;
  right: -22px;
  top: 50%;
  transform: translateY(-50%);
  color: #5c9fd4;
  font-size: 20px;
  font-weight: 700;
}

.gov-arch__platform-title {
  text-align: center;
  font-size: 15px;
  font-weight: 700;
  color: #c62828;
  margin-bottom: 12px;
  padding-bottom: 8px;
  border-bottom: 1px solid #ffcdd2;
}

.product-arch__diagram {
  display: flex;
  flex-direction: column;
  gap: 16px;
  max-width: 1100px;
  margin: 0 auto;
}

.product-arch__layer {
  padding: 24px 32px;
  border-radius: 12px;
  text-align: center;
}

.product-arch__layer-header {
  display: block;
  font-size: 15px;
  font-weight: 600;
  margin-bottom: 12px;
  padding: 8px 16px;
  border-radius: 6px;
  background: rgba(255, 255, 255, 0.5);
}

.product-arch__layer-name {
  display: block;
  font-size: 18px;
  font-weight: 700;
  margin-bottom: 12px;
}

.product-arch__layer-subtitle {
  display: block;
  font-size: 14px;
  font-weight: 600;
  margin-bottom: 12px;
  opacity: 0.9;
}

.product-arch__layer-groups {
  display: grid;
  grid-template-columns: 2fr 1fr 1fr;
  gap: 16px;
  text-align: left;
}

.product-arch__group {
  background: rgba(255, 255, 255, 0.55);
  border: 1px dashed color-mix(in srgb, var(--theme-primary) 35%, transparent);
  border-radius: 10px;
  padding: 16px;
}

.product-arch__group-name {
  display: block;
  font-size: 15px;
  font-weight: 700;
  margin-bottom: 12px;
  text-align: center;
  color: var(--theme-primary);
}

.product-arch__layer-items {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 10px;
}

.product-arch__group .product-arch__layer-items {
  justify-content: flex-start;
}

.product-arch__layer-items span {
  font-size: 13px;
  padding: 6px 14px;
  border-radius: 6px;
  background: rgba(255, 255, 255, 0.6);
}

.product-arch__layer--0 {
  background: linear-gradient(135deg, var(--theme-primary), color-mix(in srgb, var(--theme-primary) 70%, white));
  color: #fff;
}

.product-arch__layer--0 .product-arch__layer-items span {
  background: rgba(255, 255, 255, 0.2);
  color: #fff;
}

.product-arch__layer--1 {
  background: var(--theme-primary-light);
  color: var(--theme-primary);
}

.product-arch__layer--1 .product-arch__layer-items span {
  background: #fff;
  color: var(--theme-primary);
}

.product-arch__layer--2 {
  background: #fff;
  color: #333;
  border: 1px solid #eef1f6;
}

.product-arch__layer--2 .product-arch__group .product-arch__layer-items span {
  background: var(--theme-primary-light);
  color: var(--theme-primary);
}

.product-arch__layer--3 {
  background: var(--theme-primary-light);
  color: var(--theme-primary);
}

.product-arch__layer--3 .product-arch__layer-items span {
  background: #fff;
  color: var(--theme-primary);
}

.product-arch__layer--4 {
  background: #fff;
  color: #333;
  border: 1px solid #eef1f6;
}

.product-arch__layer--4 .product-arch__layer-header {
  background: var(--theme-primary-light);
  color: var(--theme-primary);
}

.product-arch__layer--4 .product-arch__layer-items span {
  background: #f7f9fc;
  color: #444;
  border: 1px solid #eef1f6;
}

.product-arch__layer--5 {
  background: #f7f9fc;
  color: #333;
  border: 1px solid #eef1f6;
}

.product-arch__layer--5 .product-arch__layer-items span {
  background: #fff;
  color: #444;
  border: 1px solid #eef1f6;
}

.product-arch__footer {
  text-align: center;
  margin-top: 24px;
  font-size: 14px;
  color: #888;
}

.product-why {
  background: #fff;
}

.product-why__grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 24px;
}

.product-why__card {
  background: #f7f9fc;
  border: 1px solid #eef1f6;
  border-radius: 12px;
  padding: 28px;
  transition: box-shadow 0.3s;
}

.product-why__card:hover {
  box-shadow: 0 8px 32px var(--theme-shadow);
}

.product-why__card h3 {
  font-size: 17px;
  font-weight: 700;
  color: #1a1a2e;
  margin-bottom: 10px;
}

.product-why__card p {
  font-size: 14px;
  color: #666;
  line-height: 1.7;
}

.product-scenario {
  background: #f7f9fc;
}

.product-scenario__grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 24px;
  margin-bottom: 32px;
}

.product-scenario__card {
  background: #fff;
  border: 1px solid #eef1f6;
  border-radius: 12px;
  padding: 28px;
}

.product-scenario__card h3 {
  font-size: 17px;
  font-weight: 700;
  color: #1a1a2e;
  margin-bottom: 10px;
}

.product-scenario__card p {
  font-size: 14px;
  color: #666;
  line-height: 1.7;
}

.product-scenario__highlights {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 24px;
}

.product-scenario__highlight {
  background: #fff;
  border: 1px solid #eef1f6;
  border-left: 3px solid var(--theme-primary);
  border-radius: 0 12px 12px 0;
  padding: 20px 24px;
}

.product-scenario__highlight h4 {
  font-size: 15px;
  font-weight: 700;
  color: var(--theme-primary);
  margin-bottom: 8px;
}

.product-scenario__highlight p {
  font-size: 13px;
  color: #666;
  line-height: 1.6;
}

.product-related {
  background: #fff;
}

.product-related__grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 24px;
}

.product-related__card {
  background: #f7f9fc;
  border: 1px solid #eef1f6;
  border-radius: 12px;
  padding: 28px 24px;
  display: flex;
  flex-direction: column;
  transition: all 0.3s;
  text-decoration: none;
  color: inherit;
}

.product-related__card:hover {
  box-shadow: 0 8px 32px rgba(0, 82, 217, 0.1);
  transform: translateY(-2px);
}

.product-related__icon {
  width: 48px;
  height: 48px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
  margin-bottom: 16px;
}

.product-related__card h3 {
  font-size: 17px;
  font-weight: 700;
  color: #1a1a2e;
  margin-bottom: 10px;
}

.product-related__card p {
  font-size: 14px;
  color: #666;
  line-height: 1.6;
  flex: 1;
  margin-bottom: 16px;
}

.product-related__link {
  font-size: 14px;
  font-weight: 600;
}

@media (max-width: 960px) {
  .product-hero__main {
    grid-template-columns: 1fr;
    gap: 8px;
  }

  .product-hero__visual {
    order: -1;
  }

  .product-hero__title {
    font-size: 32px;
  }

  .product-hero__stats-grid {
    grid-template-columns: repeat(2, 1fr);
  }

  .product-hero__stat:nth-child(2) .product-hero__stat-divider {
    display: none;
  }

  .product-features__grid {
    grid-template-columns: 1fr;
  }

  .product-why__grid {
    grid-template-columns: repeat(2, 1fr);
  }

  .product-scenario__grid,
  .product-scenario__highlights {
    grid-template-columns: 1fr;
  }

  .product-related__grid {
    grid-template-columns: 1fr;
  }

  .product-arch__layer-groups {
    grid-template-columns: 1fr;
  }

  .product-arch__layer {
    padding: 20px;
  }

  .gov-arch__label {
    flex: 0 0 40px;
    font-size: 13px;
    letter-spacing: 2px;
  }

  .gov-arch__platform {
    grid-template-columns: 1fr;
    gap: 16px;
  }

  .gov-arch__platform-group:not(:last-child) {
    margin-right: 0;
    margin-bottom: 24px;
  }

  .gov-arch__platform-group:not(:last-child)::after {
    right: auto;
    left: 50%;
    top: auto;
    bottom: -20px;
    transform: translateX(-50%) rotate(90deg);
  }

  .gov-arch__cells--grid {
    grid-template-columns: repeat(2, 1fr);
  }

  .gov-arch__cells--row .gov-arch__cell {
    min-width: calc(50% - 10px);
    flex: 1 1 calc(50% - 10px);
  }
}

@media (max-width: 560px) {
  .product-hero__stats-grid {
    grid-template-columns: 1fr;
  }

  .product-hero__stat-divider {
    display: none;
  }

  .product-hero__stat + .product-hero__stat {
    border-top: 1px solid rgba(255, 255, 255, 0.1);
  }

  .product-why__grid {
    grid-template-columns: 1fr;
  }
}
</style>
